import React, { useState } from 'react';
import { Card, Table, Button, Tag, Space, Modal, Tooltip, message } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';
import AddTeamMemberModal from './AddTeamMemberModal';
import ManageTeamMemberModal from './ManageTeamMemberModal';

const TeamManagement = ({ teamMembers, hostels, onRefresh }) => {
  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedMember, setSelectedMember] = useState(null);
  const [showManageModal, setShowManageModal] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const { user } = useAuth();

  const handleDelete = (memberId, memberName) => {
    Modal.confirm({
      title: `Delete ${memberName}?`,
      content: 'This action cannot be undone.',
      okText: 'Delete',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk: async () => {
        setDeletingId(memberId);
        try {
          await api.delete(`/admin/team-members/${memberId}`);
          message.success('Employee deleted');
          onRefresh();
        } catch (error) {
          message.error(error.response?.data?.message || 'Error deleting employee');
        } finally {
          setDeletingId(null);
        }
      },
    });
  };

  const columns = [
    { title: 'Name', dataIndex: 'name', key: 'name', render: (t) => <strong>{t}</strong> },
    { title: 'Email', dataIndex: 'email', key: 'email' },
    {
      title: 'Type',
      dataIndex: 'employeeType',
      key: 'employeeType',
      render: (t) => t ? <Tag color="blue">{String(t).charAt(0).toUpperCase() + String(t).slice(1)}</Tag> : 'N/A',
    },
    {
      title: 'Status',
      key: 'status',
      render: (_, r) => <Tag color={r.isActive ? 'green' : 'red'}>{r.isActive ? 'Active' : 'Inactive'}</Tag>,
    },
    {
      title: 'Hostel Access',
      key: 'access',
      render: (_, r) =>
        r.employeeType === 'accountant'
          ? (r.accessibleHostels?.length > 0 ? <Tag color="green">{r.accessibleHostels.length} hostel(s)</Tag> : <Tag color="red">No access</Tag>)
          : (r.hostel ? <Tag color="green">{r.hostel?.name || 'Assigned'}</Tag> : <Tag color="red">No hostel</Tag>),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Tooltip title="Manage">
            <Button type="link" icon={<EditOutlined />} onClick={() => { setSelectedMember(record); setShowManageModal(true); }} />
          </Tooltip>
          <Tooltip title="Delete">
            <Button type="link" danger icon={<DeleteOutlined />} loading={deletingId === record._id} onClick={() => handleDelete(record._id, record.name)} />
          </Tooltip>
        </Space>
      ),
    },
  ];

  return (
    <Card
      title="Employees"
      extra={
        <Button type="primary" icon={<PlusOutlined />} onClick={() => setShowAddModal(true)}>
          Add Employee
        </Button>
      }
    >
      <Table rowKey="_id" columns={columns} dataSource={teamMembers} pagination={{ pageSize: 10 }} scroll={{ x: 'max-content' }} />
      {showAddModal && (
        <AddTeamMemberModal hostels={hostels} onClose={() => setShowAddModal(false)} onSuccess={() => { onRefresh(); setShowAddModal(false); }} />
      )}
      {showManageModal && selectedMember && (
        <ManageTeamMemberModal
          member={selectedMember}
          hostels={hostels}
          onClose={() => { setShowManageModal(false); setSelectedMember(null); }}
          onSuccess={() => { onRefresh(); setShowManageModal(false); setSelectedMember(null); }}
        />
      )}
    </Card>
  );
};

export default TeamManagement;
